import React, { useState } from 'react'
import { Box, Typography } from '@material-ui/core'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../../../hooks/storeHook'
import { MarketplaceImageItem } from './marketPlaceImageItem'
import { TExistingFile, TFormValues, TNewFile } from '../productForm.types'
import { useCardPageMode } from '../../../pages/cardPage/lib/useCardPageMode'
import {
  closestCenter,
  DndContext,
  DragEndEvent, DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { SortableImage } from './sortable'
import { MAX_IMAGES } from '../lib/const'
import { StandaloneImageDropzone } from '../../../shared/ui/standaloneImageDropzone'
import { FormImagePreview } from '../../../shared/ui/formImagePreview'

export const ImageDropzoneList = observer(() => {
  const {control} = useFormContext<TFormValues>()
  const [activeId, setActiveId] = useState<string | null>(null)

  const {fields, append, remove, move} = useFieldArray<TFormValues, 'images'>({
    control,
    name: 'images'
  })

  const {mode} = useCardPageMode()

  const {productStore: {marketplaceProductCard}} = useStore()

  const watchedImages = useWatch({control, name: 'images'})

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id)
      const newIndex = fields.findIndex((field) => field.id === over.id)
      move(oldIndex, newIndex)
    }
    setActiveId(null)
  }

  const activeItem = activeId ? fields.find((field) => field.id === activeId) : null

  return (
    <Box display="flex" style={{gap: 20}}>
      <Box width={mode === 'import' ? '50%' : '100%'}>
        {mode === 'import' && <Typography align={'center'}>Фотографии в MpSkladPIM</Typography>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}>
          <SortableContext items={fields} strategy={rectSortingStrategy}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: fields.length !== 0 ? 'repeat(auto-fill, minmax(120px, 120px))' : undefined,
              gap: '16px',
              width: '100%'
            }}>
              {fields.map((field, index) => (
                <SortableImage key={field.id} id={field.id} index={index} remove={remove}/>
              ))}
              {fields.length < MAX_IMAGES && (
                <StandaloneImageDropzone onAdd={(file) => append({image: file, order: fields.length})}
                                         className={fields.length === 0 ? 'small' : undefined}/>
              )}
            </div>
          </SortableContext>

          <DragOverlay adjustScale={false}>
            {activeItem ? (
              <div style={{
                height: 200,
                width: 120,
                opacity: 0.9
              }}>
                <FormImagePreview
                  name={`images.${fields.findIndex(f => f.id === activeId)}`}
                  isMain={activeItem.order === 0}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Box>

      {mode === 'import' && <Box width={'50%'}>
        <Typography align={'center'}>Фотографии на маркетплейсе {marketplaceProductCard?.images.length === 0
                                                                 && 'не найдены'}</Typography>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 120px))',
          gap: '16px',
          width: '100%'
        }}>
          {marketplaceProductCard?.images.map((img, index) => {
            const isAdded = watchedImages?.some(
              (watched: TNewFile | TExistingFile) => 'id' in watched && watched.id === img.id)

            return <MarketplaceImageItem
              isMain={index === 0} image={img} isAdded={isAdded}
              onAdd={() => append({url: img.url, name: img.name, id: img.id, order: fields.length})}
            />
          })
          }
        </div>
      </Box>}
    </Box>
  )
})